const article = {
	slug: "meat-proxy-self-check",
	title: "Building on a good idea without becoming a meat proxy",
	date: "7 August 2026",
	updated: null,
	description:
		"An engineer's essay warned against relaying AI output you do not understand. I built a self-check on top of it, which raised a sharper question: how do you build on someone's idea without committing the same sin?",
	keywords: [
		"AI code review",
		"meat proxy",
		"AI accountability",
		"human oversight",
		"NIST AI RMF",
		"SR 11-7 effective challenge",
		"automation bias",
	],
	tldr: "Niklas Gruhn argued that forwarding AI output you do not understand makes you a useless relay, a meat proxy. I turned that into a two-minute self-check that plots you on Understanding and Accountability. Building on his idea forced the real question: crediting the source is not politeness, it is the whole difference between authoring and relaying.",
	readingTime: null,
	provenance: "A build note, August 2026.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	hero: {
		src: "/articles-media/meat-proxy-self-check.png",
		alt: "A 2x2 grid titled Meat proxy, or author, with quadrants Bluffer, Author, Pipe, Narrator.",
		caption: "The self-check plots you on two axes and lands you in one of four types.",
	},
	markdown: `Niklas Gruhn, an engineer at DeepL, wrote a short essay called "Don't be a meat proxy." The argument is simple and it stuck with me. When you forward AI output to a person without reading it, understanding it, and adding your own judgment, you have not contributed anything. The other person could have asked the model themselves. You are a relay, a meat proxy, passing messages between them and the machine.

His sharpest example is code review. If you ship code you never understood, the reviewer becomes your debugger. You did not get faster. You made someone else slower.

The piece traveled because it is true. Simon Willison picked it up, and once you have the phrase, you start seeing the behavior everywhere, in pull requests, in Slack answers, in research someone forwards you without a word of their own.

## What I built

I turned the idea into a two-minute self-check. You answer eight everyday situations. A teammate pastes an AI answer into your PR thread. A reviewer asks why a function is recursive when the model chose that, not you. Each answer moves you on two hidden axes.

![The self-check asks you to pick what you would actually do in eight realistic situations.](/articles-media/meat-proxy-questions.png)

The first axis is Understanding: do you actually comprehend the output, enough to explain it without the model in front of you. The second is Accountability: will you put your name on it, or hide behind "the AI said so." Those two axes make four types.

![The result plots you on Understanding and Accountability and names your type.](/articles-media/meat-proxy-result.png)

Pipe is the pure meat proxy, low on both. Narrator understands it but forwards it as the model's work so no blame lands on them. Bluffer is the dangerous one, it ships and vouches for what it does not understand. Author understands it and stands behind it.

The questions are behavioral on purpose. There is no visible right answer, and the Author choice almost always costs you time or forces you to admit uncertainty. You cannot game your way to a good score without doing the actual work.

## The question I did not expect

Here is where it got uncomfortable. I was building a thing on top of someone else's idea. That is the exact shape of the behavior the essay warns about. Read something, repackage it, hand it over as your own contribution.

So I had to be honest about the line. The concept and the phrase are Gruhn's. The essay's argument, the code-review framing, and the field guide are all his. What I added is the two-axis model and the four type names, and that is my elaboration, not his. The page says so in plain text, next to the model itself.

That turned out to be the whole test. A meat proxy hides the source and passes the work through. The opposite is to name what you took, add something real, and stand behind the addition. Crediting him was not politeness. It was the point.

## Where this fits, if you work in governance

None of this is new to risk teams. It usually just wears heavier clothes. The meat proxy is a named failure mode with named controls.

NIST calls it Human-AI Configuration. Its [Generative AI Profile](https://doi.org/10.6028/NIST.AI.600-1) lists over-reliance, automation bias, and loss of human oversight as a risk category in its own right, and the core [AI Risk Management Framework](https://www.nist.gov/itl/ai-risk-management-framework) asks for output that is accountable and transparent, with a clear owner.

In financial services it is older still. The model risk guidance banks run on, [SR 11-7](https://www.federalreserve.gov/supervisionreg/srletters/sr1107.htm), is built on effective challenge: critical review by someone informed enough and empowered enough to push back on a model. A meat proxy is what happens when effective challenge quietly stops.

The EU AI Act writes it into law. [Article 14](https://artificialintelligenceact.eu/article/14/) requires that a person can understand a high-risk system's limits and override it, and it names automation bias directly.

The two axes are not arbitrary. Understanding maps to human oversight, what NIST's Human-AI Configuration and Article 14 both ask for. Accountability maps to effective challenge and ownership, the heart of SR 11-7.

Map that back to the four types. A Bluffer is automation bias and a failed effective challenge. A Narrator is oversight on paper with no real ownership. A Pipe is the absence of both. An Author is what every one of these frameworks is actually asking for.

The phrase is new. The problem is not.

## The one habit

If you strip the tool down to a single rule, this is it. Before you send AI output to a human, rewrite it in your own words. If you cannot, you do not understand it yet, and you are relaying, not authoring.

Be a filter, not a pipe.

[Take the self-check](https://meatproxy.swapniltamse.com) and [read Niklas Gruhn's original essay](https://gruhn.me/blog/2026-08-03/) first. Reading the source, then adding to it, is the behavior the whole thing argues for.

## Sources

- Niklas Gruhn, "Don't be a meat proxy," 2026. [gruhn.me/blog/2026-08-03](https://gruhn.me/blog/2026-08-03/)
- NIST, AI Risk Management Framework: Generative AI Profile (NIST AI 600-1), July 2024. [doi.org/10.6028/NIST.AI.600-1](https://doi.org/10.6028/NIST.AI.600-1)
- NIST, AI Risk Management Framework (AI RMF 1.0), January 2023. [nist.gov/itl/ai-risk-management-framework](https://www.nist.gov/itl/ai-risk-management-framework)
- Federal Reserve and OCC, Supervisory Guidance on Model Risk Management (SR 11-7), 2011. [federalreserve.gov](https://www.federalreserve.gov/supervisionreg/srletters/sr1107.htm)
- EU AI Act, Article 14, Human oversight. [artificialintelligenceact.eu/article/14](https://artificialintelligenceact.eu/article/14/)`,
};

export default article;
